<template>
  <div class="video">
    <div class="video-container" style="position: relative; width: 100%;">
      <video ref="video"
             id="webcam"
             class="media-video"
             :class="{'environment': facingModeEnvironment}"
             autoplay playsinline muted
             v-show="!isFaceDetected">
      </video>
      <div class="preview" :class="{'on': isFaceDetected}" v-show="isFaceDetected">
        <img ref="outputImg" class="img-preview img-fluid" :class="{'no-flip': facingModeEnvironment}">
      </div>
      <div class="image-to-upload d-none">
        <img ref="outputImgOriginal" id="outputImgOriginal">
      </div>
      <div class="overlay-alert" v-if="showInvalidPhotoMessage || invalidFaceInfoText">
        <div class="video-info video-info-centered">
          <div>
            <Icon name="grFaceFrown" width="36" class="mb-2"/>
            <h3 class="modal-title mb-2">{{ $t('modal_pre_checkin_face_recognition.photo_step.no_face') }}</h3>
            <h4 class="modal-subtitle mb-4">
              <template v-if="invalidFaceInfoText">{{ invalidFaceInfoText }}</template>
              <template v-else>{{ $t('modal_pre_checkin_face_recognition.photo_step.no_face_text') }}</template>
            </h4>
            <b-button variant="primary" block @click="dismissError">
              {{ $t('modal_pre_checkin_face_recognition.photo_step.retake_photo') }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="overlay-alert" v-if="(detecting && !isFaceDetected) || !stream">
        <div class="d-flex align-items-center justify-content-center h-100">
          <div>
            <div class="loader">
              <div class="loader__item"></div>
              <div class="loader__item"></div>
              <div class="loader__item"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="flash" v-show="showFlash"></div>
    </div>
    <ToastSelfie ref="toastSelfie"/>
  </div>
</template>

<script>
//import cameraShutter from '../assets/audio/iphone-camera-capture.mp3'

import ToastSelfie from "@/domain/checkin/components/ToastSelfie.vue";

export default {
  name: 'WebcamStreamFace',
  components: {ToastSelfie},
  props: {
    defaultCamera: {type: String, required: false, default: 'user'},
    takingSelfie: {type: Boolean, required: false, default: false},
  },

  data() {
    return {
      debug: false,
      constraints: {
        audio: false, // we don't require to have the audio
        video: {
          width: {min: 540, ideal: window.innerHeight > window.innerWidth ? 1920 : 1000},
          height: {min: 400, ideal: window.innerHeight > window.innerWidth ? 1500 : 740},
          // aspectRatio: {ideal: 4/3},
          frameRate: {
            min: 15,
            ideal: 30,
          },
          facingMode: this.defaultCamera
        }
      },

      isFaceDetected: false,
      invalidFaceInfoText: null,
      showInvalidPhotoMessage: false,
      enableDrawDetection: false,
      showFlash: false,
      cameraAudio: null,
      detecting: false,
      runningMode: 'IMAGE',
      video: {},
      liveView: {},
      lastVideoTime: -1,
      children: [],
      detections: [],
      canvasDetection: null,
      enableCapture: false,
      stream: null,
      shotTaken: null

    };
  },
  computed: {
    facingModeEnvironment() {
      return this.constraints.video.facingMode === 'environment'
    },
    hasGetUserMedia() {
      return !!navigator.mediaDevices.getUserMedia;
    },
  },
  watch: {
    takingSelfie: function (newVal, oldVal) {
      newVal ? this.run() : null
    },
  },
  mounted() {
    this.init()
  },

  methods: {
    async init() {
      this.video = this.$refs.video
      this.liveView = this.$refs.liveView
      this.canvasDetection = this.$refs.canvasDetection
      this.shotTaken = this.$refs.outputImgOriginal

      //this.cameraAudio = new Audio(cameraShutter)

      await this.enableCam()

      /* if (this.hardwareAccelerationSupported)
         await this.initializeFaceDetector()*/

      if (!this.hasGetUserMedia) {
        console.warn("getUserMedia() is not supported by your browser");
      }
    },

    async enableCam() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(this.constraints);
        this.stream = stream
        this.video.srcObject = stream
        this.enableCapture = true

        await this.wait(500)
        this.$emit('streamAllowed')
        this.$refs.toastSelfie.show()
      } catch (error) {
        this.handleMediaStreamError(error)
      }
    },

    async run() {
      this.$refs.toastSelfie.hide()
      if (this.constraints.video.facingMode === 'user')
        this.flash(1000)

      this.detecting = true

      await this.take()
      await this.createImageToSend()

    },
    async take() {
      // Create a new canvas
      let canvas = document.createElement('CANVAS')
      canvas.width = this.video.videoWidth
      canvas.height = this.video.videoHeight

      // Draw the video frame onto the canvas
      canvas.getContext('2d').drawImage(this.video, 0, 0, this.video.videoWidth, this.video.videoHeight)

      // Convert the canvas to a data URL and set it as the source of the shotTaken image
      this.shotTaken.src = canvas.toDataURL('image/jpeg')

      // Wait for the image to load
      await new Promise(resolve => this.shotTaken.onload = resolve)
    },

    createImageToSend() {
      this.detecting = false
      this.isFaceDetected = true

      let canvas = document.createElement('CANVAS');

      // Get the aspect ratio of the image
      const aspectRatio = this.shotTaken.width / this.shotTaken.height
      const quality = 0.8

      // Set the maximum dimensions
      const maxWidth = 720
      const maxHeight = 720

      // Calculate the dimensions while maintaining the aspect ratio
      let canvasWidth, canvasHeight
      if (this.shotTaken.width > this.shotTaken.height) {
        // If width is greater than height
        canvasWidth = maxWidth
        canvasHeight = maxWidth / aspectRatio
      } else {
        // If height is greater than width or equal
        canvasHeight = maxHeight
        canvasWidth = maxHeight * aspectRatio
      }

      // Set the canvas dimensions
      canvas.width = canvasWidth
      canvas.height = canvasHeight

      // Draw the image onto the canvas
      canvas.getContext('2d').drawImage(this.shotTaken, 0, 0, canvasWidth, canvasHeight)
      this.$refs.outputImg.src = canvas.toDataURL('image/jpeg')
      canvas.toBlob(blob => {
        this.$emit('photoTaken', {faceImg: blob, faceEncoding: []})
      }, 'image/jpeg', quality)
    },

    stopMediaStreamTrack() {
      this.video.srcObject = null;
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
      }
    },
    switchCamera() {
      this.constraints.video.facingMode = this.constraints.video.facingMode === 'user' ? 'environment' : 'user'
      this.stopMediaStreamTrack()
      this.init()
    },
    flash(timeout) {
      this.showFlash = true
      setTimeout(() => {
        this.showFlash = false
      }, timeout)
    },

    handleMediaStreamError(error) {
      this.$emit('streamNotAllowed', {error: error.name})
    },

    pause() {
      this.video.pause()
    },
    dismissError() {
      this.showInvalidPhotoMessage = false
      this.retakePhoto()
    },
    retakePhoto(hasError) {
      this.$emit('photoRetake', {error: hasError})
    },

    setInvalidState(infoText) {
      // server side invalidation
      this.invalidFaceInfoText = infoText
    },

    resumeVideo() {
      this.video.play()
      this.isFaceDetected = false
      this.invalidFaceInfoText = null
    },
    /* detectWebGLContext() {
       // Create canvas element. The canvas is not added to the
       // document itself, so it is never displayed in the
       // browser window.
       const canvas = document.createElement("canvas")
       // Get WebGLRenderingContext from canvas element.
       const gl = canvas.getContext("webgl")
           || canvas.getContext("experimental-webgl")
       // Report the result.
       return !!(gl && gl instanceof WebGLRenderingContext)
     }*/
  },
  beforeDestroy() {
    this.pause()
    this.stopMediaStreamTrack();
  }
}
</script>

<style>




.highlighter {
  background: rgba(0, 255, 0, 0.25);
  border: 1px dashed #fff;
  z-index: 1;
  position: absolute;
}


.key-point {
  position: absolute;
  z-index: 1;
  width: 3px;
  height: 3px;
  background-color: #ff0000;
  /* border: 1px solid #ffffff; */
  border-radius: 50%;
  display: block;
}
</style>
